// WebSocket wrapper (architecture §9, protocol §4): opens `wsUrl` (relative URLs resolve against
// the server base, see net/server-url.ts; http→ws, https→wss), `binaryType = 'arraybuffer'`,
// sends HELLO on open, decodes every message with `decodeServerMessage(data, quantOf(welcome))`,
// emits typed events, sends PING every 2 s after WELCOME and smooths the RTT from PONG
// (`ema = 0.8·ema + 0.2·sample`).
// A message that fails to decode emits `protocolError` and closes the socket (code 4004).
//
// Money rooms (money-mode §2.1, §4; protocol §5.1–5.2): `sendPlay(nick, skin, ticket)` sends PLAY
// with the entry ticket; `cashOutStart()` / `cashOutCancel()` send the hold messages;
// CASH_OUT_STATE and CASH_OUT_DONE are emitted as `cashOutState` (with the arrival time, for the
// locally animated ring) and `cashOutDone`.
//
// `sent` reports every gameplay intent actually written to an open socket (PLAY, SPLIT, EJECT,
// SPECIAL, SPECTATE, CASH_OUT_START, CASH_OUT_CANCEL — whoever sent it, hold-W repeats included);
// INPUT, PING, OPTIONS and HELLO are not reported. net/handlers.ts turns it into client-bus events.
//
// Close events: exactly one `close` per opened socket. A close the client asks for (`close()`, or
// `connect()` replacing the current socket) is reported at once with `byClient: true` and the
// socket's handlers are detached, so nothing from the old socket arrives afterwards.
//
// Implementer: client net/game. Consumers: net/discovery.ts (Connector drives connect/close),
// net/handlers.ts (subscribes), input/input-sender.ts (`send`), app.ts (`rttMs` for the stats
// readout). `Emitter` lives in events/emitter.ts and is re-exported here for existing imports.

import {
  C2S,
  type CashOutDoneMessage,
  type CashOutStateMessage,
  CLOSE,
  DEFAULT_QUANT,
  type DeathMessage,
  decodeClientMessage,
  decodeServerMessage,
  encodeCashOutCancel,
  encodeCashOutStart,
  encodeHello,
  encodePing,
  encodePlay,
  type ErrorMessage,
  type FrameMessage,
  INTERPOLATION,
  type MatchResultMessage,
  NET_TIMING,
  type NoticeMessage,
  type PartyMessage,
  PROTOCOL_VERSION,
  type Quant,
  quantOf,
  S2C,
  type ServerMessage,
  type SpawnedMessage,
  type WelcomeMessage,
} from '@ballio/shared';
import { Emitter } from '../events/emitter.js';
import { serverWsUrl } from './server-url.js';

export { Emitter, type EmitterOptions } from '../events/emitter.js';

// ---------------------------------------------------------------------------------------------
// GameSocket
// ---------------------------------------------------------------------------------------------

export interface SocketCloseInfo {
  /** WebSocket close code (1006 when the connection failed). */
  readonly code: number;
  readonly reason: string;
  /** A WELCOME had arrived on this socket. */
  readonly welcomed: boolean;
  /** The ERROR message received just before the close, if any. */
  readonly error: ErrorMessage | null;
  /** The close was requested by this client (`close()`). */
  readonly byClient: boolean;
}

export interface SocketEvents {
  /** The socket opened and HELLO was sent. */
  open: { readonly url: string };
  welcome: WelcomeMessage;
  /** `atMs` = `performance.now()` at arrival (the tick clock's input). */
  frame: { readonly msg: FrameMessage; readonly atMs: number };
  spawned: SpawnedMessage;
  death: DeathMessage;
  notice: NoticeMessage;
  party: PartyMessage;
  matchResult: MatchResultMessage;
  /** ERROR (the server closes right after). */
  serverError: ErrorMessage;
  /** Money rooms: a player's cash-out hold changed; `atMs` = `performance.now()` at arrival. */
  cashOutState: { readonly msg: CashOutStateMessage; readonly atMs: number };
  /** Money rooms: your cash-out completed (you are now an observer). */
  cashOutDone: CashOutDoneMessage;
  /**
   * A gameplay intent was written to the open socket: `op` is its C2S opcode (PLAY, SPLIT,
   * EJECT, SPECIAL, SPECTATE, CASH_OUT_START, CASH_OUT_CANCEL). Not for INPUT/PING/OPTIONS/HELLO.
   * `ticket` is true for a PLAY that carried an entry ticket.
   */
  sent: { readonly op: number; readonly ticket: boolean };
  /** `rttMs` = the smoothed RTT (= `GameSocket.rttMs`); `sampleMs` = this PONG's raw RTT. */
  pong: { readonly rttMs: number; readonly sampleMs: number };
  close: SocketCloseInfo;
  /** A server message failed to decode; the socket closes itself next. */
  protocolError: { readonly error: unknown };
}

export const SOCKET_STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  OPEN: 'open',
  CLOSED: 'closed',
} as const;
export type SocketState = (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE];

export interface GameSocketOptions {
  /** HELLO clientBuild (≤ 32 bytes). */
  readonly clientBuild: string;
  /** Default PROTOCOL_VERSION; tests force 0 to provoke 4001 (roadmap M2 #11). */
  readonly protocolVersion?: number;
  /** Default `globalThis.WebSocket`. */
  readonly WebSocketImpl?: typeof WebSocket;
  /** Server base for relative `wsUrl`s (net/server-url.ts); `""` (default) = this page. */
  readonly baseUrl?: string;
  /** Default `performance.now`. */
  readonly now?: () => number;
}

/** WebSocket.OPEN (fakes in tests may not define the static constants). */
const WS_OPEN = 1;

/** C2S opcodes reported by the `sent` event (gameplay intents). */
const REPORTED_OPS: ReadonlySet<number> = new Set<number>([
  C2S.PLAY,
  C2S.SPLIT,
  C2S.EJECT,
  C2S.SPECIAL,
  C2S.SPECTATE,
  C2S.CASH_OUT_START,
  C2S.CASH_OUT_CANCEL,
]);

/** Whether encoded PLAY bytes carry an entry ticket (PLAY is rare: decoding it is fine). */
function hasTicket(bytes: Uint8Array): boolean {
  try {
    const msg = decodeClientMessage(bytes);
    return msg.op === C2S.PLAY && msg.ticket !== undefined;
  } catch {
    return false;
  }
}

/** A PONG reporting more than this is a stale or bogus echo, not an RTT. */
const MAX_RTT_SAMPLE_MS = 60_000;

/**
 * Resolves a discovery `wsUrl` (relative like `/ws?room=ffa-1`, or absolute) to a WebSocket URL:
 * relative URLs resolve against the server base `baseUrl` (`""`/omitted = the page), and http(s)
 * becomes ws(s). See `serverWsUrl`.
 */
export function resolveWsUrl(wsUrl: string, baseUrl = ''): string {
  const page =
    typeof globalThis.location !== 'undefined' ? globalThis.location.href : 'http://localhost/';
  return serverWsUrl(wsUrl, baseUrl, page);
}

function defaultNow(): number {
  return performance.now();
}

export class GameSocket {
  readonly events = new Emitter<SocketEvents>();
  state: SocketState = SOCKET_STATE.IDLE;
  /** WELCOME of the current socket, null before it arrives. */
  welcome: WelcomeMessage | null = null;
  /** Smoothed RTT in ms (0 until the first PONG). */
  rttMs = 0;
  protected readonly opts: GameSocketOptions;

  private ws: WebSocket | null = null;
  /** The URL of the current socket (after resolving). */
  private url = '';
  private currentQuant: Quant = DEFAULT_QUANT;
  private lastError: ErrorMessage | null = null;
  private hasRtt = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  /** Increments per connect(); guards the deferred failure report. */
  private seq = 0;
  private readonly now: () => number;

  constructor(opts: GameSocketOptions) {
    this.opts = opts;
    this.now = opts.now ?? defaultNow;
  }

  /** The quantisation of the current WELCOME (DEFAULT_QUANT before). */
  get quant(): Quant {
    return this.currentQuant;
  }

  get protocolVersion(): number {
    return this.opts.protocolVersion ?? PROTOCOL_VERSION;
  }

  /** The resolved URL of the current (or last) socket; "" before the first connect. */
  get currentUrl(): string {
    return this.url;
  }

  /**
   * Closes any current socket (1000) and opens `wsUrl`. `authToken` (money rooms: the signed-in
   * player's access token, auth/auth.ts) goes into this socket's HELLO; omit it for an anonymous
   * socket (free rooms).
   */
  connect(wsUrl: string, authToken?: string): void {
    this.close(CLOSE.NORMAL);
    const seq = ++this.seq;
    const helloToken = authToken === '' ? undefined : authToken;
    this.welcome = null;
    this.currentQuant = DEFAULT_QUANT;
    this.lastError = null;
    this.rttMs = 0;
    this.hasRtt = false;
    this.state = SOCKET_STATE.CONNECTING;

    let ws: WebSocket;
    try {
      this.url = resolveWsUrl(wsUrl, this.opts.baseUrl);
      const Impl = this.opts.WebSocketImpl ?? globalThis.WebSocket;
      ws = new Impl(this.url);
      ws.binaryType = 'arraybuffer';
    } catch (err) {
      // A bad URL or a refused construction: report it like a failed connection, but never
      // re-entrantly from inside connect().
      queueMicrotask(() => {
        if (this.seq !== seq || this.ws !== null) return;
        this.state = SOCKET_STATE.CLOSED;
        this.events.emit('close', {
          code: CLOSE.ABNORMAL,
          reason: err instanceof Error ? err.message : String(err),
          welcomed: false,
          error: null,
          byClient: false,
        });
      });
      return;
    }

    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.state = SOCKET_STATE.OPEN;
      this.sendRaw(ws, encodeHello(this.opts.clientBuild, this.protocolVersion, helloToken));
      this.events.emit('open', { url: this.url });
    };
    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return;
      this.onMessage(ev.data);
    };
    ws.onclose = (ev: CloseEvent) => {
      if (this.ws !== ws) return;
      this.finish(ev.code, ev.reason, false);
    };
    ws.onerror = () => {
      // The close event that follows carries the outcome.
    };
  }

  /** Client-initiated close (default 1000: room change). */
  close(code = 1000, reason = ''): void {
    const ws = this.ws;
    if (!ws) return;
    try {
      ws.close(code, reason);
    } catch {
      // Codes outside 1000 / 3000–4999 throw; close plainly instead.
      try {
        ws.close();
      } catch {
        // already closing
      }
    }
    this.finish(code, reason, true);
  }

  /** Sends one encoded client message; dropped unless open. Intents are reported as `sent`. */
  send(bytes: Uint8Array<ArrayBuffer>): void {
    const ws = this.ws;
    if (!ws || this.state !== SOCKET_STATE.OPEN) return;
    if (!this.sendRaw(ws, bytes)) return;
    const op = bytes[0] ?? 0;
    if (REPORTED_OPS.has(op)) {
      this.events.emit('sent', { op, ticket: op === C2S.PLAY && hasTicket(bytes) });
    }
  }

  /**
   * PLAY (protocol §5.1). `ticket` = the money-room entry ticket id from the buy-in
   * (money-mode §2.1); omit it in free rooms. Dropped unless open.
   */
  sendPlay(nick: string, skinId: string, ticket?: string): void {
    this.send(
      ticket === undefined ? encodePlay(nick, skinId) : encodePlay(nick, skinId, 0, ticket),
    );
  }

  /** Money rooms: the cash-out key went down (money-mode §4). The server runs the timer. */
  cashOutStart(): void {
    this.send(encodeCashOutStart());
  }

  /** Money rooms: the cash-out key was released (money-mode §4). */
  cashOutCancel(): void {
    this.send(encodeCashOutCancel());
  }

  // -------------------------------------------------------------------------------------------

  /** True when the bytes were handed to the WebSocket. */
  private sendRaw(ws: WebSocket, bytes: Uint8Array<ArrayBuffer>): boolean {
    if (ws.readyState !== WS_OPEN) return false;
    try {
      ws.send(bytes);
      return true;
    } catch {
      // The socket is going away; its close event reports it.
      return false;
    }
  }

  private finish(code: number, reason: string, byClient: boolean): void {
    const ws = this.ws;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.onerror = null;
    this.ws = null;
    this.stopPing();
    this.state = SOCKET_STATE.CLOSED;
    this.events.emit('close', {
      code,
      reason,
      welcomed: this.welcome !== null,
      error: this.lastError,
      byClient,
    });
  }

  private onMessage(data: unknown): void {
    let bytes: Uint8Array;
    if (data instanceof ArrayBuffer) bytes = new Uint8Array(data);
    else if (ArrayBuffer.isView(data))
      bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    else {
      this.protocolFailure(new Error('the server sent a non-binary message'));
      return;
    }
    let msg: ServerMessage;
    try {
      msg = decodeServerMessage(bytes, this.currentQuant);
    } catch (error) {
      this.protocolFailure(error);
      return;
    }
    const atMs = this.now();
    switch (msg.op) {
      case S2C.WELCOME:
        this.welcome = msg;
        this.currentQuant = quantOf(msg);
        this.startPing();
        this.events.emit('welcome', msg);
        return;
      case S2C.FRAME:
        this.events.emit('frame', { msg, atMs });
        return;
      case S2C.SPAWNED:
        this.events.emit('spawned', msg);
        return;
      case S2C.DEATH:
        this.events.emit('death', msg);
        return;
      case S2C.PONG:
        this.onPong(msg.clientTimeMs, atMs);
        return;
      case S2C.NOTICE:
        this.events.emit('notice', msg);
        return;
      case S2C.PARTY:
        this.events.emit('party', msg);
        return;
      case S2C.MATCH_RESULT:
        this.events.emit('matchResult', msg);
        return;
      case S2C.CASH_OUT_STATE:
        this.events.emit('cashOutState', { msg, atMs });
        return;
      case S2C.CASH_OUT_DONE:
        this.events.emit('cashOutDone', msg);
        return;
      case S2C.ERROR:
        this.lastError = msg;
        this.events.emit('serverError', msg);
        return;
    }
  }

  private protocolFailure(error: unknown): void {
    this.events.emit('protocolError', { error });
    this.close(CLOSE.MALFORMED, 'malformed server message');
  }

  private onPong(clientTimeMs: number, atMs: number): void {
    // u32 arithmetic: the PING carried the low 32 bits of now().
    const sample = ((Math.floor(atMs) >>> 0) - clientTimeMs) >>> 0;
    if (sample > MAX_RTT_SAMPLE_MS) return;
    const keep = INTERPOLATION.pingEmaKeep;
    this.rttMs = this.hasRtt ? keep * this.rttMs + (1 - keep) * sample : sample;
    this.hasRtt = true;
    this.events.emit('pong', { rttMs: this.rttMs, sampleMs: sample });
  }

  private startPing(): void {
    this.stopPing();
    this.ping();
    this.pingTimer = setInterval(() => this.ping(), NET_TIMING.pingIntervalSec * 1000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private ping(): void {
    const ws = this.ws;
    if (!ws || this.state !== SOCKET_STATE.OPEN) return;
    this.sendRaw(ws, encodePing(this.now()));
  }
}
